/**
 * The tenant's provider record: its identity, contacts, governance rules and
 * the discipline list the Identity step offers.
 *
 * One read serves both screens that need it, so a tenant's disciplines and
 * its provider details cannot disagree about what was last saved.
 *
 * @module components/admin/useProviderSettings
 */

import { useEffect, useState } from "react";

import type { ProgrammePlatformClient } from "@/platform";
import type { TenantProviderSettings } from "@/server/types";
import type { ProviderContact, ProviderSettings } from "@/types";

export interface ProviderSettingsState {
  /** The saved record, or null until the first read answers. */
  record: TenantProviderSettings | null;
  /** The edited copy the form writes into. */
  draft: ProviderSettings;
  setDraft: (update: (current: ProviderSettings) => ProviderSettings) => void;
  updateContact: (
    contactKey: "validationContact",
    field: keyof ProviderContact,
    value: string,
  ) => void;
  /** True until the first read answers, and again while a save is in flight. */
  pending: boolean;
  /** What the last read or provider save said. Empty when there is nothing. */
  feedback: string;
  /** Why the record could not be read at all. Empty once there is one. */
  loadError: string;
  /** Reads the record again after a failed read. */
  reload: () => void;
  save: () => Promise<void>;
  /**
   * Replaces the disciplines alone, leaving every other setting as saved.
   * Resolves to whether they were saved; refuses while one is in flight.
   */
  saveDisciplines: (next: string[]) => Promise<boolean>;
  /** A discipline write is in flight. */
  disciplinesSaving: boolean;
  /**
   * What the last discipline write said. Its own line rather than the one
   * above: the two are separate screens, and a refusal belongs on the screen
   * that asked for it.
   */
  disciplineFeedback: string;
}

export interface ProviderSettingsOptions {
  /** Called after a write the audit trail should then show. */
  onWritten?: () => void;
}

/**
 * @param client - The platform client to read and write through.
 * @param tenantId - The tenant whose provider record this is.
 * @param options - What to do after a successful write.
 * @returns The record, its draft, and the two saves.
 *
 * @example
 * ```tsx
 * const provider = useProviderSettings(client, tenant.id, { onWritten: reloadAudit });
 * ```
 */
export function useProviderSettings(
  client: ProgrammePlatformClient,
  tenantId: string,
  { onWritten = undefined }: ProviderSettingsOptions = {},
): ProviderSettingsState {
  const [loaded, setLoaded] = useState<{
    tenantId: string;
    record: TenantProviderSettings | null;
    draft: ProviderSettings;
    pending: boolean;
    feedback: string;
  }>({
    tenantId,
    record: null,
    draft: { name: "" },
    pending: true,
    feedback: "",
  });

  // Nothing edited yet: seeding this with the tenant made the empty draft win
  // over the loaded one, so the form opened blank and a save wiped the rest.
  const [edits, setEdits] = useState<{
    tenantId: string | null;
    draft: ProviderSettings;
  }>({
    tenantId: null,
    draft: { name: "" },
  });

  const [disciplineFeedback, setDisciplineFeedback] = useState("");
  const [disciplinesSaving, setDisciplinesSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    client
      .getTenantProviderSettings()
      .then((next) => {
        if (active) {
          setLoaded({
            tenantId,
            record: next,
            draft: next.settings,
            pending: false,
            feedback: "",
          });
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setLoaded({
            tenantId,
            record: null,
            draft: { name: "" },
            pending: false,
            feedback: cause instanceof Error ? cause.message : "Unable to load provider settings",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [client, tenantId, attempt]);

  const isCurrentTenant = loaded.tenantId === tenantId;
  const record = isCurrentTenant ? loaded.record : null;
  let draft = isCurrentTenant ? loaded.draft : { name: "" };
  if (edits.tenantId === tenantId) {
    draft = edits.draft;
  }
  const pending = isCurrentTenant ? loaded.pending : true;
  const feedback = isCurrentTenant ? loaded.feedback : "";

  const setDraft = (update: (current: ProviderSettings) => ProviderSettings): void => {
    setEdits({ tenantId, draft: update(draft) });
  };

  const updateContact = (
    contactKey: "validationContact",
    field: keyof ProviderContact,
    value: string,
  ): void => {
    setDraft((current) => ({
      ...current,
      [contactKey]: { ...current[contactKey], [field]: value },
    }));
  };

  const save = async (): Promise<void> => {
    if (!record || !isCurrentTenant || pending) {
      return;
    }
    setLoaded((current) => ({ ...current, pending: true, feedback: "" }));
    try {
      const updated = await client.updateTenantProviderSettings({ ...record, settings: draft });
      setEdits({ tenantId, draft: updated.settings });
      setLoaded({
        tenantId,
        record: updated,
        draft: updated.settings,
        pending: false,
        feedback: "Provider details saved.",
      });
      onWritten?.();
    } catch (cause) {
      setLoaded((current) => ({
        ...current,
        pending: false,
        feedback: cause instanceof Error ? cause.message : "Unable to save provider details",
      }));
    }
  };

  const saveDisciplines = async (next: string[]): Promise<boolean> => {
    if (!record || !isCurrentTenant || disciplinesSaving) {
      return false;
    }
    setDisciplinesSaving(true);
    setDisciplineFeedback("");
    try {
      const updated = await client.updateTenantProviderSettings({
        ...record,
        settings: { ...record.settings, disciplines: next },
      });
      setLoaded((current) => ({
        ...current,
        record: updated,
        draft: { ...current.draft, disciplines: updated.settings.disciplines },
      }));
      setEdits((current) =>
        current.tenantId === tenantId
          ? { ...current, draft: { ...current.draft, disciplines: updated.settings.disciplines } }
          : current,
      );
      onWritten?.();
      return true;
    } catch (cause) {
      setDisciplineFeedback(
        cause instanceof Error ? cause.message : "Unable to save tenant disciplines",
      );
      return false;
    } finally {
      setDisciplinesSaving(false);
    }
  };

  return {
    record,
    draft,
    setDraft,
    updateContact,
    pending,
    feedback,
    loadError: record || pending ? "" : feedback,
    reload: () => {
      setLoaded((current) => ({ ...current, pending: true, feedback: "" }));
      setAttempt((current) => current + 1);
    },
    save,
    saveDisciplines,
    disciplinesSaving,
    disciplineFeedback,
  };
}
